<template>
  <div class="nutrition-copilot">
    <div class="nc-head">
      <div>
        <h3 class="nc-title">
          <span class="nc-ai">✨ AI</span> Nutrition copilot
        </h3>
        <p class="nc-sub">
          Turns your team's compliance data into a quick read, coaching actions,
          and a ready-to-send player reminder. You review and edit before sending.
        </p>
      </div>
      <button class="nc-btn primary" @click="generate" :disabled="loading || !clubId">
        {{ loading ? "Thinking…" : result ? "Regenerate" : "Generate summary" }}
      </button>
    </div>

    <p v-if="error" class="nc-error">{{ error }}</p>

    <div v-if="result && result.refused" class="nc-refused">
      {{ result.reason || "That request was outside the scope of nutrition support." }}
    </div>

    <template v-else-if="result">
      <!-- Coach-facing summary -->
      <div class="nc-summary">
        <p>{{ result.summary }}</p>
      </div>

      <!-- Suggested actions -->
      <div v-if="result.actions && result.actions.length" class="nc-actions">
        <h4>Suggested actions</h4>
        <ul>
          <li v-for="(a, i) in result.actions" :key="i">{{ a }}</li>
        </ul>
      </div>

      <!-- Welfare-safe player draft -->
      <div class="nc-draft">
        <div class="nc-draft-head">
          <h4>Player reminder <span class="nc-tag">welfare-safe · editable</span></h4>
          <button class="nc-btn ghost" @click="copyDraft">
            {{ copied ? "Copied ✓" : "Copy" }}
          </button>
        </div>
        <textarea v-model="editableDraft" rows="4" class="nc-textarea"></textarea>
      </div>

      <p class="nc-disclaimer">
        AI-generated from your compliance data — check it before sending.
      </p>
    </template>

    <p v-else-if="!loading" class="nc-empty">
      Generate an AI read of your squad's nutrition and a reminder you can send.
    </p>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";
import { AI } from "@/services/ai-service.js";

export default {
  name: "NutritionCopilot",
  props: {
    clubId: { type: [String, Number], default: null },
    teamId: { type: [String, Number], default: null },
    teamLabel: { type: String, default: "the squad" },
  },
  data() {
    return {
      loading: false,
      error: "",
      result: null,
      editableDraft: "",
      copied: false,
    };
  },
  methods: {
    async generate() {
      if (!this.clubId) return;
      this.loading = true;
      this.error = "";
      this.copied = false;
      try {
        const [trendsRes, dashRes] = await Promise.all([
          Nutrition.getComplianceTrends(this.clubId, this.teamId, 30),
          Nutrition.getTeamNutritionDashboard(this.clubId, this.teamId).catch(() => null),
        ]);

        const trends = (trendsRes && (trendsRes.data ?? trendsRes)) || {};
        const pts = trends.data || [];
        const last = pts.length ? pts[pts.length - 1] : {};
        const first = pts.length ? pts[0] : {};
        const num = (v) => Number(v) || 0;

        const dash = dashRes && (dashRes.data ?? dashRes);
        const dashData = (dash && dash.data) || dash || {};
        const attention = (dashData.needs_attention || [])
          .slice(0, 8)
          .map((p) => ({ name: p.name, reason: p.reason }));

        const context = {
          action: "nutritionCoachCopilot",
          team_label: this.teamLabel,
          days: 30,
          total_players: num(last.total_players) || num(dashData.total_players),
          logging_rate: num(last.logging_rate),
          compliance_rate: num(last.compliance_rate),
          compliance_delta: num(last.compliance_rate) - num(first.compliance_rate),
          hydration_rate: num(last.hydration_rate),
          players_attention: attention,
        };

        const aiRes = await AI.find(context);
        const payload = aiRes.data ?? aiRes;
        if (payload && payload.error) {
          this.error = payload.error;
        } else {
          this.result = payload;
          this.editableDraft = (payload && payload.draft_message) || "";
        }
      } catch (e) {
        this.error = "Could not generate a summary. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async copyDraft() {
      try {
        await navigator.clipboard.writeText(this.editableDraft || "");
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (e) {
        this.error = "Copy failed — select the text and copy manually.";
      }
    },
  },
};
</script>

<style scoped>
.nutrition-copilot {
  background: linear-gradient(180deg, #fbfbff 0%, #fff 60%);
  border: 1px solid #e6e6f2;
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 18px;
}
.nc-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.nc-title {
  margin: 0 0 3px;
  font-size: 1.1rem;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;
}
.nc-ai {
  background: #efeafc;
  color: #6b3fd4;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.nc-sub {
  margin: 0;
  font-size: 0.84rem;
  color: #6b7278;
  max-width: 460px;
}
.nc-btn {
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
}
.nc-btn.primary {
  background: var(--brand-color, #2b61d6);
  color: #fff;
}
.nc-btn.primary:disabled {
  opacity: 0.5;
  cursor: default;
}
.nc-btn.ghost {
  background: #fff;
  border-color: #d5d9dd;
  color: #444;
  padding: 5px 12px;
  font-size: 0.8rem;
}
.nc-error {
  color: #c0392b;
  font-size: 0.86rem;
  margin: 8px 0 0;
}
.nc-refused {
  color: #8a6d3b;
  background: #fcf8e3;
  border: 1px solid #f2e3b3;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.86rem;
  margin-top: 12px;
}
.nc-empty {
  color: #7a7f84;
  font-size: 0.88rem;
  margin: 14px 0 2px;
}
.nc-summary {
  margin-top: 14px;
  font-size: 0.94rem;
  color: #2c3136;
  line-height: 1.5;
}
.nc-summary p {
  margin: 0;
}
.nc-actions {
  margin-top: 14px;
}
.nc-actions h4,
.nc-draft h4 {
  margin: 0 0 6px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7278;
}
.nc-actions ul {
  margin: 0;
  padding-left: 18px;
}
.nc-actions li {
  font-size: 0.9rem;
  color: #2c3136;
  margin-bottom: 4px;
  line-height: 1.45;
}
.nc-draft {
  margin-top: 16px;
}
.nc-draft-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.nc-tag {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: #2b8a3e;
  background: #e4f7ec;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.68rem;
  margin-left: 6px;
}
.nc-textarea {
  width: 100%;
  border: 1px solid #d5d9dd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.92rem;
  color: #2c3136;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
}
.nc-disclaimer {
  margin: 8px 0 0;
  font-size: 0.76rem;
  color: #9298a0;
}
</style>
